import React, { useEffect, useState, useRef } from 'react';
import { Media } from '../../../../models/Media/Media';
import { ButtonPrimary } from '../../Button/ButtonPrimary';
import { InputField } from '../../InputField/InputField';
import styles from './edit-media-modal.module.scss';
import { toast } from 'react-toastify';
import { SimpleModal } from '../SimpleModal/SimpleModal';
import { Formik, Form, Field } from 'formik';

interface EditMediaModalProps {
  media: Media | null;
  onClose: () => void;
  onUpdate: (updatedMedia: Media) => void;
}

export const EditMediaModal: React.FC<EditMediaModalProps> = ({
  media,
  onClose,
  onUpdate
}) => {
  // State for the new image preview URL
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // When the modal opens for a new image, reset the preview
    if (media) {
      setPreviewUrl(null);
    }
  }, [media]);

  if (!media) return null;

  return (
    <SimpleModal
      isOpen={!!media}
      onClose={onClose}
      title={`Izmeni Sliku: ${media.name}`}
    >
      <Formik
        initialValues={{
          name: media.name || '',
          file: undefined as File | undefined
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const updatedMedia = await Media.update(media.id, {
              name: values.name,
              file: values.file
            });
            toast.success('Slika je uspešno izmenjena!');
            onUpdate(updatedMedia);
          } catch (error) {
            // Check if the error is a Laravel validation error
            if (
              typeof error === 'object' &&
              error !== null &&
              'response' in error &&
              typeof (error as any).response === 'object' &&
              (error as any).response !== null &&
              (error as any).response.status === 422 &&
              (error as any).response.data &&
              (error as any).response.data.errors
            ) {
              // Get the first error message for the 'name' field
              const nameError = (error as any).response.data.errors.name?.[0];
              if (nameError) {
                toast.error('Ime mora biti jedinstveno. ');
              } else {
                // Fallback for other potential validation errors
                toast.error('Došlo je do greške pri validaciji.');
              }
            } else {
              // Generic error for network issues or other server errors
              toast.error('Izmena slike nije uspela. Proverite konekciju.');
            }
            console.error('Failed to update media:', error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>
            <div className={styles.container}>
              <label htmlFor="name">Naziv Slike</label>
              <Field
                id="name"
                name="name"
                as={InputField}
                placeholder="Unesite naziv slike"
              />

              <hr />

              <div className={styles.previewsContainer}>
                <div className={styles.previewColumn}>
                  <p>Trenutna slika:</p>
                  <img src={media.fullUrl} alt="Current" />
                </div>
                {/* 3. Make this entire column clickable */}
                <div
                  className={styles.previewColumn}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <p>Nova slika:</p>
                  {previewUrl ? (
                    <img src={previewUrl} alt="New Preview" />
                  ) : (
                    <div className={styles.placeholder}>
                      <span>Kliknite da izaberete fajl</span>
                    </div>
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                name="file"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  if (file) {
                    setFieldValue('file', file);
                    setPreviewUrl(URL.createObjectURL(file));
                  }
                }}
              />
            </div>
            <div className={styles.actions}>
              <ButtonPrimary type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Menjanje...' : 'Sačuvaj Izmene'}
              </ButtonPrimary>
            </div>
          </Form>
        )}
      </Formik>
    </SimpleModal>
  );
};
